import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import '../../styles/MyTransactions.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faReceipt } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import Loader from '../Loader';

function MyTransactions({ user, limit, showFilter = false, showViewAll = false, account }) {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          let fetchedTransactions = (userData.transactions || [])
            .map((t, idx) => ({ ...t, mapIndex: idx }))
            .filter((t) => {
              if (!account) return t.account !== 'Trading';
              const accounts = Array.isArray(account) ? account : [account];
              return accounts.includes(t.account);
            })
            .sort((a, b) => new Date(b.date) - new Date(a.date));
          
          if (filterType && filterType !== 'all') {
            fetchedTransactions = fetchedTransactions.filter(t => t.type === filterType);
          }
          
          if (limit) {
            fetchedTransactions = fetchedTransactions.slice(0, limit);
          }
          
          setTransactions(fetchedTransactions);
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [user, limit, filterType, account]);

  if (loading) {
    return <Loader />;
  }

  const handleFilter = (type) => {
    setFilterType(type);
  };

  return (
    <div className="my-transactions">
      {showFilter && (
        <div className="my-trans-filters">
          <button 
            className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => handleFilter('all')}
          >
            All
          </button>
          <button 
            className={`filter-btn ${filterType === 'credit' ? 'active' : ''}`}
            onClick={() => handleFilter('credit')}
          >
            Credits
          </button>
          <button 
            className={`filter-btn ${filterType === 'debit' ? 'active' : ''}`}
            onClick={() => handleFilter('debit')}
          >
            Debits
          </button>
        </div>
      )}
      {transactions.length === 0 ? (
        <div className="my-trans-empty">
          <FontAwesomeIcon icon={faReceipt} className="my-trans-empty-icon" />
          <h3>No transactions yet</h3>
          <span className="my-trans-hint">Make a deposit to get started</span>
        </div>
      ) : (
        <ul className="my-trans-list">
          {transactions.map((transaction, index) => (
            <li 
              key={index} 
              className={`my-trans-item ${transaction.type}`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/transactions/${user.uid}/${transaction.mapIndex}`);
              }}
            >
              <div className="my-trans-icon">
                <FontAwesomeIcon 
                  icon={transaction.type === 'credit' ? faArrowUp : faArrowDown} 
                />
              </div>
              <div className="my-trans-details">
                <div className="my-trans-desc">{transaction.description}</div>
                <div className="my-trans-date">
                  {moment(transaction.date).format('MMM D, YYYY')}
                </div>
              </div>
              <div className="my-trans-amount">
                {transaction.type === 'credit' ? '+' : '-'}${transaction.amount.toFixed(2)}
              </div>
            </li>
          ))}
        </ul>
      )}
      {showViewAll && transactions.length > 0 && (
        <div className="my-trans-view-all" onClick={() => navigate('/transactions')}>
          View all transactions
        </div>
      )}
    </div>
  );
}


export default MyTransactions;